import { useEffect, useRef, useState } from "react";
import { X, Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import { fetchMajorsList } from "@/services/profile";

type SaveState = "idle" | "saving" | "saved" | "error";

interface MajorsSelectorProps {
  items: string[];
  onChange: (items: string[]) => void;
  onSave: (items: string[]) => Promise<void> | void;
  maxItems?: number;
}

const MajorsSelector = ({ items, onChange, onSave, maxItems = 8 }: MajorsSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [allMajors, setAllMajors] = useState<string[]>([]);
  const [status, setStatus] = useState<SaveState>("idle");
  const [error, setError] = useState<string | null>(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    fetchMajorsList()
      .then(setAllMajors)
      .catch(() => {});
  }, []);

  const trySave = async (next: string[]) => {
    try {
      setStatus("saving");
      setError(null);
      await onSave(next);
      setStatus("saved");
      window.setTimeout(() => setStatus("idle"), 1500);
    } catch (e: any) {
      setStatus("error");
      setError(String(e?.message || "Save failed"));
    }
  };

  const toggle = async (major: string) => {
    let next: string[];
    if (items.includes(major)) {
      next = items.filter(m => m !== major);
    } else {
      if (items.length >= maxItems) return;
      next = [...items, major];
    }
    onChange(next);
    await trySave(next);
  };

  const remove = async (major: string) => {
    const next = items.filter(m => m !== major);
    onChange(next);
    await trySave(next);
  };

  return (
    <div>
      <div className="text-xs text-muted-foreground mb-1">Preferred Majors</div>

      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {items.map(m => (
            <span
              key={m}
              className="inline-flex items-center gap-1 rounded-full border bg-muted px-2.5 py-0.5 text-xs"
            >
              {m}
              <button
                type="button"
                onClick={() => remove(m)}
                className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                aria-label={`Remove ${m}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
              "placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              !items.length && "text-muted-foreground/60 italic"
            )}
          >
            {items.length
              ? `${items.length} major${items.length === 1 ? "" : "s"} selected`
              : "Search majors (e.g., Business, Biology)"}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command>
            <CommandInput placeholder="Type to search majors..." />
            <CommandList>
              <CommandEmpty>No majors found.</CommandEmpty>
              <CommandGroup>
                {allMajors.map(major => {
                  const selected = items.includes(major);
                  return (
                    <CommandItem
                      key={major}
                      value={major}
                      onSelect={() => toggle(major)}
                      disabled={!selected && items.length >= maxItems}
                    >
                      <Check
                        className={cn("mr-2 h-4 w-4", selected ? "opacity-100" : "opacity-0")}
                      />
                      {major}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <div className={`text-xs mt-1 ${error ? "text-destructive" : "text-muted-foreground"}`}>
        {error
          ? error
          : status === "saving"
            ? "Saving\u2026"
            : status === "saved"
              ? "Saved"
              : items.length >= maxItems
                ? `Maximum ${maxItems} majors`
                : "\u00A0"}
      </div>
    </div>
  );
};

export default MajorsSelector;
